import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";

import {sitesAC} from "../../../../store/branches/sites/actionCreators";
import {devicesAC} from "../../../../store/branches/devices/actionCreators";
import {DevicesApi} from "../../../../services/api/devicesApi";
import {selectSitesState, selectUserState} from "../../../../store/selectors";
import {SensorsApi} from "../../../../services/api/sensorsApi";
import {useCurrentSelection} from "../../../../hooks/useCurrentSelection";

import {
    aggregateStatusCounts,
    buildProblemNodeStatus,
    buildSensorTree,
    buildSiteTree,
    DeviceStatus,
    ExplorerNode,
    filterTree,
    filterTreeByStatus,
    findAncestorKeys,
    ProblemSensor,
    problemSensorNodeKey,
    SensorApiGroup,
    updateNodeChildren,
} from "./buildTree";
import ExplorerTree from "./ExplorerTree";
import StatusChip from "./StatusChip";
import {SearchIcon} from "./icons";

import classes from "./DevicesExplorer.module.scss";

const STATUS_REFRESH_DEBOUNCE_MS = 500;

export const EXPLORER_PANEL_WIDTH = 300;

interface DevicesExplorerProps {
    sidebarWidth: number;
}

const DevicesExplorer: React.FC<DevicesExplorerProps> = ({sidebarWidth}) => {
    const dispatch = useDispatch();
    const {sitesData, isSelected} = useSelector(selectSitesState);
    const {ws_notify} = useSelector(selectUserState);
    const {device: currentDevice} = useCurrentSelection();

    const [treeData, setTreeData] = useState<ExplorerNode[]>([]);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
    const [selectedKey, setSelectedKey] = useState<string>();
    const [query, setQuery] = useState("");
    const [deviceStatus, setDeviceStatus] = useState<Record<number, DeviceStatus>>({});
    const [problemSensors, setProblemSensors] = useState<Record<number, ProblemSensor[]>>({});
    const [activeStatusFilter, setActiveStatusFilter] = useState<DeviceStatus | null>(null);

    useEffect(() => {
        if (!isSelected) {
            dispatch(sitesAC.fetchSites());
        }
    }, [dispatch, isSelected]);

    const fetchStatusRollup = () => {
        DevicesApi.getStatusRollup().then((data) => {
            setDeviceStatus((data.devices || {}) as unknown as Record<number, DeviceStatus>);
            setProblemSensors((data.problemSensors || {}) as unknown as Record<number, ProblemSensor[]>);
        });
    };

    useEffect(() => {
        fetchStatusRollup();
    }, []);

    // Live updates: reuse the notifications websocket the app already keeps
    // open for the notification bell (HeaderNotifications, always mounted)
    // rather than opening a second connection. Any relevant event (device
    // online/offline, alarm/warning on/off) flows through this same
    // `ws_notify` state — on change, debounce a refetch of the authoritative
    // classification rather than trying to incrementally parse the socket
    // payload (whose event_type is a display string, not the raw code).
    const isFirstNotifyRun = useRef(true);
    useEffect(() => {
        if (isFirstNotifyRun.current) {
            isFirstNotifyRun.current = false;
            return undefined;
        }
        const timeout = setTimeout(fetchStatusRollup, STATUS_REFRESH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [ws_notify]);

    useEffect(() => {
        setTreeData((previousNodes) => buildSiteTree(sitesData?.results || [], previousNodes));
    }, [sitesData]);

    // Highlight + reveal whichever device is currently being viewed (Devices
    // grid selection, Sensor Dashboard, Sensor Graphs all share the same
    // "current device" state), mirroring the UIV2 concept's own
    // expand-to-selection behavior.
    useEffect(() => {
        if (!currentDevice?.id) {
            return;
        }
        const site = (sitesData?.results || []).find(
            (candidate) => candidate.devices?.some((d) => d.id === currentDevice.id),
        );
        if (!site) {
            return;
        }
        setSelectedKey(`device-${currentDevice.id}`);
        setExpanded((prev) => new Set(prev).add(`site-${site.id}`));
    }, [currentDevice?.id, sitesData]);

    const deviceCount = useMemo(
        () => (sitesData?.results || []).reduce((sum, site) => sum + (site.devices?.length || 0), 0),
        [sitesData],
    );

    const {bySite: siteCounts, totals: statusTotals} = useMemo(
        () => aggregateStatusCounts(sitesData?.results || [], deviceStatus),
        [sitesData, deviceStatus],
    );

    const visibleTreeData = useMemo(
        () => filterTree(filterTreeByStatus(treeData, activeStatusFilter, deviceStatus, expanded), query.trim()),
        [treeData, query, activeStatusFilter, deviceStatus, expanded],
    );

    const problemNodeStatus = useMemo(() => buildProblemNodeStatus(problemSensors), [problemSensors]);

    // If the count behind the active filter drops to zero (its last matching
    // device changed status), clear the filter automatically rather than
    // leaving the user stuck looking at an empty tree with no visible chip
    // left to click to get back out.
    useEffect(() => {
        if (activeStatusFilter && statusTotals[activeStatusFilter] === 0) {
            setActiveStatusFilter(null);
        }
    }, [activeStatusFilter, statusTotals]);

    const handleStatusFilterClick = (status: DeviceStatus) => {
        setActiveStatusFilter((prev) => (prev === status ? null : status));
    };

    const fetchDeviceSensors = (node: ExplorerNode) => {
        if (!node.deviceId) {
            return;
        }
        setLoadingKeys((prev) => new Set(prev).add(node.key));
        SensorsApi.getSelectedSensorsByDevice(String(node.deviceId))
            .then((data: SensorApiGroup[]) => {
                const groups = buildSensorTree(data, node.locationId, node.deviceId as number);
                setTreeData((origin) => updateNodeChildren(origin, node.key, groups));

                // Auto-reveal whichever sensor(s)/group(s) are the actual
                // reason this device is in warning/alarm, the moment its
                // subtree becomes available — same "auto-expand to what
                // matters" idea as the current-device effect above, one
                // level deeper, triggered by the tree loading rather than a
                // click.
                const problems = problemSensors[node.deviceId as number] || [];
                if (problems.length > 0) {
                    setExpanded((prev) => {
                        const next = new Set(prev);
                        problems.forEach((problem) => {
                            const targetKey = problemSensorNodeKey(problem);
                            if (!targetKey) {
                                return;
                            }
                            findAncestorKeys(groups, targetKey).forEach((key) => next.add(key));
                        });
                        return next;
                    });
                }
            })
            .finally(() => {
                setLoadingKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(node.key);
                    return next;
                });
            });
    };

    const handleToggle = (node: ExplorerNode) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(node.key)) {
                next.delete(node.key);
            } else {
                next.add(node.key);
                if (node.nodeType === "device") {
                    // Durably pin the containing site open too, not just the
                    // device — a device can become visible/toggleable purely
                    // via forceExpand (an active search or status filter)
                    // without its site ever having been individually
                    // clicked open. Without this, the site (and everything
                    // in it, including this device) would collapse the
                    // moment that search/filter clears, even though the
                    // user explicitly opened this device.
                    next.add(`site-${node.locationId}`);
                    if (!node.children) {
                        fetchDeviceSensors(node);
                    }
                }
            }
            return next;
        });
    };

    const handleSelect = (node: ExplorerNode) => {
        setSelectedKey(node.key);
        if (node.nodeType === "device" || node.nodeType === "sensor") {
            if (node.deviceId) {
                dispatch(devicesAC.notificationSelect({locationId: node.locationId, deviceId: node.deviceId}));
            }
        } else {
            handleToggle(node);
        }
    };

    return (
        <div className={classes.wrap} style={{left: sidebarWidth}}>
            <div className={classes.header}>
                <div className={classes.headerTop}>
                    <span className={classes.title}>Explorer</span>
                    <span className={classes.counts}>{sitesData?.results?.length || 0} sites · {deviceCount} devices</span>
                </div>
                <div className={classes.statusChipsRow}>
                    {(["healthy", "warning", "alarm", "offline"] as const).map((status) => (
                        <StatusChip
                            key={status}
                            status={status}
                            count={statusTotals[status]}
                            showLabel
                            onClick={() => handleStatusFilterClick(status)}
                            active={activeStatusFilter === status}
                            dimmed={!!activeStatusFilter && activeStatusFilter !== status}
                        />
                    ))}
                </div>
                <div className={classes.searchWrap}>
                    <span className={classes.searchIcon}><SearchIcon size={14}/></span>
                    <input
                        className={classes.searchInput}
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search sites, devices…"
                    />
                </div>
            </div>
            <div className={classes.treeWrap}>
                <ExplorerTree
                    nodes={visibleTreeData}
                    expanded={expanded}
                    forceExpand={query.trim().length > 0 || !!activeStatusFilter}
                    loadingKeys={loadingKeys}
                    selectedKey={selectedKey}
                    deviceStatus={deviceStatus}
                    problemNodeStatus={problemNodeStatus}
                    siteCounts={siteCounts}
                    onToggle={handleToggle}
                    onSelect={handleSelect}
                />
            </div>
        </div>
    );
};

export default DevicesExplorer;
